import JSZip from 'jszip';
import {
  ZipExpansionBudget,
  assertZipEntryCount,
  readZipEntryText,
  type ZipReadLimits,
} from '@vif/parsers';
import {
  BackupTooLargeError,
  RESTORE_MAX_ENTRY_BYTES,
  RESTORE_MAX_TOTAL_BYTES,
  serializedBlocksBytes,
  utf8ByteLength,
} from './backup-limits.js';
import { ALL_STORES, type VifDB } from './db.js';
import type { BlockRow } from './types.js';

/**
 * .vif backup (PRD §10): a JSZip archive holding the FULL library. Export
 * is all-or-nothing; import is a full restore (wipe + write, atomic) that
 * the shell puts behind an explicit confirmation dialog. No merge, no
 * selective restore, no cloud.
 *
 * Injectable exportedAt stamps both the manifest and every zip entry. Export →
 * wipe → import → export round-trips with identical CONTENT — the round-trip
 * test compares unzipped payloads, not archive bytes, since the same library can
 * legitimately serialize to different ZIP bytes across runs/environments.
 */

export const BACKUP_SCHEMA_VERSION = 1;

/**
 * W-4 (Sol M-03, web residue): restore inflates UNTRUSTED input, so every
 * entry read goes through @vif/parsers' bounded incremental zip reader — a
 * bomb aborts DURING decompression, never after the allocation.
 *
 * The per-entry / total byte bounds are the RESTORE half of the round-trip
 * invariant derived in backup-limits.ts (import bounds one document's
 * serialized blocks at 32 MiB → a maximal 5-slot library serializes blocks.json
 * to <160 MiB, which clears the 256 MiB / 512 MiB caps with ≥1.5× headroom).
 * Import, export (the grandfather guard below) and restore all read the SAME
 * numbers, so the three can never drift out of agreement.
 *
 *  - maxEntries 64        — the archive is exactly 9 canonical files
 *                           (manifest + 8 stores); 64 leaves ~7× forward slack.
 *  - maxEntryBytes        — RESTORE_MAX_ENTRY_BYTES (256 MiB).
 *  - maxTotalBytes        — RESTORE_MAX_TOTAL_BYTES (512 MiB).
 *  - maxCompressionRatio 2048 — ABOVE DEFLATE's theoretical maximum (~1032:1),
 *                           so a legitimate single-layer archive can never trip
 *                           it (a 2 M-word repeated-paste backup compresses
 *                           extremely well); the absolute byte bounds are the
 *                           real guard, with the 64 KiB floor for tiny entries.
 */
const BACKUP_ZIP_LIMITS: ZipReadLimits = {
  maxEntries: 64,
  maxEntryBytes: RESTORE_MAX_ENTRY_BYTES,
  maxTotalBytes: RESTORE_MAX_TOTAL_BYTES,
  maxCompressionRatio: 2048,
  ratioCheckMinBytes: 64 * 1024,
};

export type BackupErrorCode = 'corrupt' | 'unsupported-version';

const BACKUP_ERROR_MESSAGES: Readonly<Record<BackupErrorCode, string>> = {
  corrupt: "This isn't a valid .vif backup — the file is damaged or not a Vif export.",
  'unsupported-version':
    'This backup was made by a newer version of Vif — update the app, then import again.',
};

export class BackupError extends Error {
  readonly code: BackupErrorCode;

  constructor(code: BackupErrorCode) {
    super(BACKUP_ERROR_MESSAGES[code]);
    this.name = 'BackupError';
    this.code = code;
  }
}

interface Manifest {
  readonly schemaVersion: number;
  readonly exportedAt: number;
  readonly appVersion: string;
}

/** Store name → archive path. Keyed stores (settings, kv) dump [key, value]
 * pairs; keyPath stores dump their rows. */
const STORE_FILES: Readonly<Record<(typeof ALL_STORES)[number], string>> = {
  documents: 'data/documents.json',
  blocks: 'data/blocks.json',
  positions: 'data/positions.json',
  bookmarks: 'data/bookmarks.json',
  sessions: 'data/sessions.json',
  settings: 'data/settings.json',
  kv: 'data/kv.json',
  legacyResume: 'data/legacy-resume.json',
};
/** Out-of-line-key stores serialized as [key, value] pairs. */
const PAIR_STORES = new Set<(typeof ALL_STORES)[number]>(['settings', 'kv']);

export interface ExportOptions {
  readonly appVersion: string;
  /** Injectable for deterministic output; defaults to Date.now(). */
  readonly exportedAt?: number;
}

/** The per-entry / total decompressed-byte caps restore enforces. Injectable so
 * the grandfather guard's rejection + naming path can be exercised without
 * allocating a 256 MB fixture; production always uses the real restore caps. */
export interface RestoreCaps {
  readonly maxEntryBytes: number;
  readonly maxTotalBytes: number;
}

const DEFAULT_RESTORE_CAPS: RestoreCaps = {
  maxEntryBytes: RESTORE_MAX_ENTRY_BYTES,
  maxTotalBytes: RESTORE_MAX_TOTAL_BYTES,
};

export async function exportBackup(
  db: VifDB,
  options: ExportOptions,
  caps: RestoreCaps = DEFAULT_RESTORE_CAPS,
): Promise<Uint8Array> {
  const exportedAt = options.exportedAt ?? Date.now();
  const date = new Date(exportedAt);
  const manifest: Manifest = {
    schemaVersion: BACKUP_SCHEMA_VERSION,
    exportedAt,
    appVersion: options.appVersion,
  };

  // Serialize every entry first (each store's JSON is what the archive holds
  // and what restore inflates back to), so the grandfather guard can weigh the
  // real decompressed sizes before we commit to producing a .vif file.
  const entries: Array<{ path: string; json: string }> = [
    { path: 'manifest.json', json: JSON.stringify(manifest, null, 1) },
  ];
  for (const store of ALL_STORES) {
    let payload: unknown;
    if (PAIR_STORES.has(store)) {
      const [keys, values] = await Promise.all([db.getAllKeys(store), db.getAll(store)]);
      payload = keys.map((key, i) => [key, values[i]]);
    } else {
      payload = await db.getAll(store);
    }
    entries.push({ path: STORE_FILES[store], json: JSON.stringify(payload, null, 1) });
  }

  // W-4 GRANDFATHER: a document persisted before the import-time byte invariant
  // (or via a lower-level API that predates it) can exceed it. Never hand the
  // user a .vif that importBackup would reject — measure every entry against the
  // SAME restore caps and fail first, with a typed error naming the culprit.
  await assertExportWithinRestoreCaps(db, entries, caps);

  const zip = new JSZip();
  for (const { path, json } of entries) zip.file(path, json, { date });
  return zip.generateAsync({ type: 'uint8array', compression: 'DEFLATE' });
}

/**
 * W-4 grandfather guard: reject an export whose decompressed entries would trip
 * the restore caps, before writing a byte the user believes is a safe backup.
 * Only blocks.json can realistically breach, so on a breach we pinpoint the
 * heaviest document to make the error actionable ("remove <title>").
 */
async function assertExportWithinRestoreCaps(
  db: VifDB,
  entries: ReadonlyArray<{ path: string; json: string }>,
  caps: RestoreCaps,
): Promise<void> {
  let total = 0;
  let overEntry = false;
  for (const { json } of entries) {
    const bytes = utf8ByteLength(json);
    total += bytes;
    if (bytes > caps.maxEntryBytes) overEntry = true;
  }
  if (!overEntry && total <= caps.maxTotalBytes) return;

  const title = (await heaviestDocumentByBlocks(db)) ?? 'a document';
  const message = overEntry
    ? `"${title}" is too large to include in a backup — remove it from your library, then export again.`
    : `Your library is too large to back up — remove "${title}", then export again.`;
  throw new BackupTooLargeError(message, title);
}

/** The title of the document whose serialized blocks are the largest single
 * contribution to blocks.json — the one to remove to bring the archive under
 * the caps. */
async function heaviestDocumentByBlocks(db: VifDB): Promise<string | undefined> {
  const [blocks, docs] = await Promise.all([db.getAll('blocks'), db.getAll('documents')]);
  const rowsByDoc = new Map<string, BlockRow[]>();
  for (const row of blocks) {
    const list = rowsByDoc.get(row.docId);
    if (list === undefined) rowsByDoc.set(row.docId, [row]);
    else list.push(row);
  }
  let heaviestId: string | undefined;
  let heaviestBytes = -1;
  for (const [docId, rows] of rowsByDoc) {
    const bytes = serializedBlocksBytes(rows);
    if (bytes > heaviestBytes) {
      heaviestBytes = bytes;
      heaviestId = docId;
    }
  }
  if (heaviestId === undefined) return undefined;
  return docs.find((doc) => doc.id === heaviestId)?.title ?? heaviestId;
}

function parseJsonArray(text: string): unknown[] {
  const parsed: unknown = JSON.parse(text);
  if (!Array.isArray(parsed)) throw new BackupError('corrupt');
  return parsed;
}

/* P1-F3: row-level schema validation. Every row of every store is checked
 * against its storage/types.ts shape BEFORE the destructive transaction
 * opens — one malformed row anywhere rejects the whole archive with zero
 * writes. Unknown extra fields are tolerated (same-schema forward slack);
 * wrong-typed known fields are not. */
const isString = (v: unknown): boolean => typeof v === 'string';
const isFiniteNumber = (v: unknown): boolean => typeof v === 'number' && Number.isFinite(v);
/** Integer fields (store indices/keys): a fractional idx corrupts key order. */
const isIndex = (v: unknown): boolean => typeof v === 'number' && Number.isInteger(v);
const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const optional =
  (check: (v: unknown) => boolean) =>
  (v: unknown): boolean =>
    v === undefined || check(v);

const DOCUMENT_FORMATS: ReadonlySet<unknown> = new Set([
  'pdf',
  'docx',
  'epub',
  'txt',
  'md',
  'paste',
]);
const BLOCK_KINDS: ReadonlySet<unknown> = new Set(['paragraph', 'chapter-heading']);

/** Store → row validator. Pair stores (settings, kv) validate the VALUE —
 * their keys are already checked as strings by the pair-shape pass. */
const ROW_VALIDATORS: Readonly<Record<(typeof ALL_STORES)[number], (row: unknown) => boolean>> = {
  documents: (r) =>
    isRecord(r) &&
    isString(r.id) &&
    isString(r.title) &&
    DOCUMENT_FORMATS.has(r.format) &&
    isString(r.lang) &&
    optional(isString)(r.langOverride) &&
    isFiniteNumber(r.wordCount) &&
    isFiniteNumber(r.addedAt) &&
    isString(r.contentHash) &&
    isFiniteNumber(r.size),
  blocks: (r) =>
    isRecord(r) &&
    isString(r.docId) &&
    isIndex(r.idx) &&
    BLOCK_KINDS.has(r.kind) &&
    (r.level === null || isIndex(r.level)) &&
    isString(r.text),
  positions: (r) =>
    isRecord(r) && isString(r.docId) && isIndex(r.wordIdx) && isFiniteNumber(r.updatedAt),
  bookmarks: (r) =>
    isRecord(r) &&
    isString(r.docId) &&
    isIndex(r.wordIdx) &&
    isString(r.label) &&
    isFiniteNumber(r.createdAt),
  sessions: (r) =>
    isRecord(r) &&
    isString(r.id) &&
    optional(isString)(r.docId) &&
    isString(r.date) &&
    isFiniteNumber(r.words) &&
    isFiniteNumber(r.activeMs) &&
    isFiniteNumber(r.avgWpm),
  settings: (v) => isRecord(v) && Number.isInteger(v.schemaVersion) && isRecord(v.data),
  kv: (v) => isString(v),
  legacyResume: (r) =>
    isRecord(r) &&
    isString(r.legacyHash) &&
    optional(isString)(r.fileName) &&
    optional(isFiniteNumber)(r.lastPosition) &&
    Array.isArray(r.bookmarks) &&
    r.bookmarks.every(isFiniteNumber),
};

/** Full restore: validates the archive, then wipes and rewrites every store
 * in a single transaction — a failed import leaves the library untouched. */
/** Bounded entry read: any violation (or decompression failure) is the same
 * typed 'corrupt' rejection the import-error UX already renders. */
async function readBoundedEntry(entry: object, budget: ZipExpansionBudget): Promise<string> {
  try {
    return await readZipEntryText(entry, budget);
  } catch {
    throw new BackupError('corrupt');
  }
}

export async function importBackup(db: VifDB, bytes: Uint8Array): Promise<Manifest> {
  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(bytes);
  } catch {
    throw new BackupError('corrupt');
  }
  // W-4: bound the declared entry count before touching any entry, and route
  // every entry read below through the shared expansion budget.
  const budget = new ZipExpansionBudget(BACKUP_ZIP_LIMITS);
  try {
    assertZipEntryCount(Object.keys(zip.files).length, BACKUP_ZIP_LIMITS);
  } catch {
    throw new BackupError('corrupt');
  }

  const manifestFile = zip.file('manifest.json');
  if (manifestFile === null) throw new BackupError('corrupt');
  let manifest: Manifest;
  try {
    manifest = JSON.parse(await readBoundedEntry(manifestFile, budget)) as Manifest;
  } catch {
    throw new BackupError('corrupt');
  }
  if (!Number.isInteger(manifest.schemaVersion)) throw new BackupError('corrupt');
  if (manifest.schemaVersion > BACKUP_SCHEMA_VERSION) throw new BackupError('unsupported-version');
  if (!isFiniteNumber(manifest.exportedAt) || !isString(manifest.appVersion)) {
    throw new BackupError('corrupt');
  }

  // Parse and validate everything BEFORE touching the database.
  const contents = new Map<(typeof ALL_STORES)[number], unknown[]>();
  for (const store of ALL_STORES) {
    const file = zip.file(STORE_FILES[store]);
    if (file === null) throw new BackupError('corrupt');
    const text = await readBoundedEntry(file, budget);
    try {
      contents.set(store, parseJsonArray(text));
    } catch {
      throw new BackupError('corrupt');
    }
  }
  for (const pairStore of PAIR_STORES) {
    for (const entry of contents.get(pairStore) ?? []) {
      if (!Array.isArray(entry) || entry.length !== 2 || typeof entry[0] !== 'string') {
        throw new BackupError('corrupt');
      }
    }
  }
  for (const store of ALL_STORES) {
    const validate = ROW_VALIDATORS[store];
    for (const entry of contents.get(store) ?? []) {
      const row = PAIR_STORES.has(store) ? (entry as [string, unknown])[1] : entry;
      if (!validate(row)) throw new BackupError('corrupt');
    }
  }

  const tx = db.transaction([...ALL_STORES], 'readwrite');
  for (const store of ALL_STORES) {
    const objectStore = tx.objectStore(store);
    void objectStore.clear();
    const rows = contents.get(store) ?? [];
    for (const row of rows) {
      if (PAIR_STORES.has(store)) {
        const [key, value] = row as [string, never];
        void objectStore.put(value, key);
      } else {
        void objectStore.put(row as never);
      }
    }
  }
  await tx.done;
  return manifest;
}
